import { AlertTriangle, CheckCircle2, FlaskConical, HardDrive, Info, Loader2, ShieldCheck, XCircle, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { compactAgo, exactTime } from '@/modules/backups/format';
import type { Tone } from '@/modules/backups/health';
import { innerSurface, toneText, toneTint, toneWash } from '@/components/surface/tones';
import { Disclosure, Panel, SectionHeader, StatusGlyph, StatusPill } from '@/components/surface/ui';
import { humanDuration } from '@/modules/monitoring/format';
import type { BackupVerification, BackupVerificationOverview, VerificationStatus } from '@/types/api';

const statusTone: Record<VerificationStatus, Tone> = {
  passed: 'success',
  failed: 'danger',
  inconclusive: 'warning',
  running: 'muted',
};

const statusIcon: Record<VerificationStatus, LucideIcon> = {
  passed: CheckCircle2,
  failed: XCircle,
  inconclusive: AlertTriangle,
  running: Loader2,
};

/**
 * Whether the backups would actually restore, in the space of a sidebar.
 *
 * ## The latest result by default, the rest one click away
 *
 * The question most visits ask is "did the last test pass?", so that is what shows. Every
 * source's latest test and the full history of checks are behind their own sections —
 * except a test that failed or could not run, which is never hidden: a failure folded away behind
 * a chevron is a failure nobody sees.
 *
 * ## Could-not-run is amber, never red
 *
 * An unreachable server or a full disk stops a test from running. That says nothing about the
 * backup, so it is shown as a reason to look, not as a broken backup. Red is kept for a test that
 * proved a copy would not restore.
 */
export function VerificationPanel({ verification }: { verification: BackupVerificationOverview }) {
  const [showHow, setShowHow] = useState(false);
  const { summary } = verification;

  const failing = verification.restore_tests.filter((test) => test.status === 'failed');
  const couldNotRun = verification.restore_tests.filter((test) => test.status === 'inconclusive');
  const latest = summary.last_restore_test;
  const needsALook = [...failing, ...couldNotRun].filter((test) => test.id !== latest?.id);

  const pill: { tone: Tone | 'primary'; label: string; pulse?: boolean } = summary.running
    ? { tone: 'primary', label: 'Checking now', pulse: true }
    : failing.length > 0
      ? { tone: 'danger', label: `${failing.length} failing` }
      : verification.restore_tests.length === 0
        ? { tone: 'muted', label: 'Not tested yet' }
        : couldNotRun.length > 0
          ? { tone: 'warning', label: `${couldNotRun.length} could not run` }
          : { tone: 'success', label: 'All passing' };

  return (
    <Panel id="verification">
      <SectionHeader
        icon={ShieldCheck}
        iconTone={pill.tone === 'primary' ? 'success' : pill.tone}
        title="Restore verification"
        badge={<StatusPill tone={pill.tone} label={pill.label} pulse={pill.pulse ?? false} />}
        actions={
          <button
            type="button"
            onClick={() => setShowHow((open) => !open)}
            aria-expanded={showHow}
            aria-label="How restore verification works"
            title="How it works"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <Info className="size-4" aria-hidden />
          </button>
        }
      />

      {showHow && (
        <div className="mx-5 mb-3 space-y-2 rounded-lg bg-foreground/[0.04] px-3.5 py-3 text-[13px] text-muted-foreground">
          <p>
            Every backup is checked at its destination as soon as it is written. Once a week the newest
            copy of each source is fetched back onto its own server and checked byte for byte, and its
            database is loaded into a scratch database and read back table by table, then removed. Your
            live data is never touched.
          </p>
          <p>
            A check that <span className="font-medium text-foreground">could not run</span> (the server
            was unreachable, or had no room for the scratch copy) says nothing about the backup, so it
            is marked amber rather than red.
          </p>
        </div>
      )}

      <div className="px-5 pb-4">
        {verification.history.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">
            Nothing verified yet. The next backup is checked as soon as it is written, and you can test
            any backup with <span className="font-medium text-foreground">Test restore</span>.
          </p>
        ) : latest === null ? (
          <p className="text-[13px] text-muted-foreground">No restore test yet. The first runs within a day of a backup.</p>
        ) : (
          <>
            <p className="mb-2 text-xs text-muted-foreground">Latest restore test</p>
            <TestResult test={latest} emphasised />
          </>
        )}

        {needsALook.length > 0 && (
          <div className="mt-3 space-y-2">
            {needsALook.map((test) => (
              <TestResult key={test.id} test={test} emphasised />
            ))}
          </div>
        )}
      </div>

      {verification.restore_tests.length > 0 && (
        <Disclosure title="Each source" meta={`${summary.sources_verified} of ${summary.sources_backed_up} passed this week`}>
          <ul className="space-y-1 px-3">
            {verification.restore_tests.map((test) => (
              <li key={test.id}>
                <TestResult test={test} />
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {verification.history.length > 0 && (
        <Disclosure
          title="Verification history"
          meta={`${verification.history.length} ${verification.history.length === 1 ? 'check' : 'checks'}`}
        >
          <p className="px-5 pb-2 text-xs text-muted-foreground">
            {summary.last_30_days.storage.passed} storage {summary.last_30_days.storage.passed === 1 ? 'check' : 'checks'}{' '}
            and {summary.last_30_days.restore.passed} restore {summary.last_30_days.restore.passed === 1 ? 'test' : 'tests'} passed in
            the last 30 days.
          </p>
          <ol className="px-5 pt-1">
            {verification.history.map((item, index) => (
              <TimelineEntry key={item.id} item={item} last={index === verification.history.length - 1} />
            ))}
          </ol>
        </Disclosure>
      )}
    </Panel>
  );
}

/** One restore test: who, how it ended, when, and what it found. */
function TestResult({ test, emphasised = false }: { test: BackupVerification; emphasised?: boolean }) {
  const tone = statusTone[test.status];
  const failed = test.status === 'failed';

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg px-2 py-2',
        emphasised && (tone === 'success' || tone === 'muted' ? cn(innerSurface, 'px-2.5 py-2.5') : cn(toneWash[tone], 'px-2.5 py-2.5')),
      )}
    >
      <StatusGlyph icon={statusIcon[test.status]} tone={tone} spin={test.status === 'running'} label={test.status_label} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-medium">{test.source_name}</p>
          <time className="shrink-0 text-xs text-muted-foreground" dateTime={test.started_at} title={exactTime(test.started_at)}>
            {compactAgo(test.started_at)}
          </time>
        </div>
        <p className="text-xs">
          <span className={cn('font-medium', toneText[tone])}>{test.status_label}</span>
          {test.duration_seconds !== null && (
            <span className="text-muted-foreground"> · took {humanDuration(test.duration_seconds)}</span>
          )}
        </p>
        {test.detail !== '' && (
          <p
            className={cn('mt-0.5 text-xs', failed ? 'text-danger' : 'text-muted-foreground', !failed && !emphasised && 'line-clamp-1')}
            title={test.detail}
          >
            {test.detail}
          </p>
        )}
      </div>
    </div>
  );
}

function TimelineEntry({ item, last }: { item: BackupVerification; last: boolean }) {
  const KindIcon = item.kind === 'restore' ? FlaskConical : HardDrive;
  const tone = statusTone[item.status];

  return (
    <li className="relative flex gap-3 pb-3 last:pb-1">
      {!last && <span aria-hidden className="absolute bottom-0 left-[11px] top-7 w-px bg-foreground/[0.08]" />}
      <span className={cn('relative inline-flex size-6 shrink-0 items-center justify-center rounded-full', toneTint[tone])} aria-hidden>
        <KindIcon className="size-3" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="min-w-0 truncate text-[13px]">
            <span className="font-medium">{item.kind_label}</span>
            <span className="text-muted-foreground"> · {item.source_name}</span>
          </p>
          <time className="shrink-0 text-xs text-muted-foreground" dateTime={item.started_at} title={exactTime(item.started_at)}>
            {compactAgo(item.started_at)}
          </time>
        </div>
        <p className="text-xs">
          <span className={cn('font-medium', toneText[tone])}>{item.status_label}</span>
          {item.detail !== '' && (
            <span className={item.status === 'failed' ? 'text-danger' : 'text-muted-foreground'}>: {item.detail}</span>
          )}
        </p>
      </div>
    </li>
  );
}
